/**
 * Small allow-list HTML sanitizer for the Interest-to-Income visual editor.
 *
 * Used at both write time (in InlineTextEditor commit) and read time (in
 * the editor's rich-text fields) as defense-in-depth. Uses the browser's HTML parser via
 * <template> so no third-party dependency is required.
 *
 * Allowed tags (see ALLOWED_TAGS constant below for the source of truth):
 *   B, STRONG, I, EM, U, A, BR, SPAN, FONT, UL, OL, LI, P, DIV
 *
 * Allowed attributes:
 *   href/target/rel on <a> (href must start with http, https, mailto, tel,
 *       # or /; javascript: is stripped)
 *   style on <span>/<p>/<div>/<li> (only color, text-align, font-weight,
 *       background-color, font-style)
 *   color/size on <font>  (execCommand('foreColor', …) emits <font color>)
 *
 * Everything else — event handlers, <script>, <style>, <iframe>, <img>,
 * class, id, data-*, and arbitrary CSS declarations — is stripped.
 */

const ALLOWED_TAGS = new Set([
    "B", "STRONG", "I", "EM", "U", "A", "BR", "SPAN", "FONT",
    "UL", "OL", "LI", "P", "DIV",
]);

/**
 * Regex that matches an opening tag for any of the allowed tags. Exported
 * so `InlineTextEditor` can decide "is this HTML worth sanitizing" without
 * hardcoding a second list (which drifts).
 */
export const ALLOWED_TAG_REGEX = new RegExp(
    "<(" + [...ALLOWED_TAGS].join("|").toLowerCase() + ")\\b",
    "i"
);

const ALLOWED_ATTRS = {
    A: new Set(["href", "target", "rel"]),
    SPAN: new Set(["style"]),
    P: new Set(["style"]),
    DIV: new Set(["style"]),
    LI: new Set(["style"]),
    FONT: new Set(["color", "size"]),
};

const ALLOWED_CSS_PROPS = new Set([
    "color", "text-align", "font-weight", "background-color", "font-style",
]);

const SAFE_HREF = /^(https?:|mailto:|tel:|\/|#)/i;

function filterStyle(css) {
    if (!css) return "";
    return String(css)
        .split(";")
        .map((s) => s.trim())
        .filter(Boolean)
        .map((decl) => {
            const idx = decl.indexOf(":");
            if (idx < 0) return null;
            const prop = decl.slice(0, idx).trim().toLowerCase();
            const val = decl.slice(idx + 1).trim();
            if (!ALLOWED_CSS_PROPS.has(prop)) return null;
            // Block url()/expression()/javascript: in values
            if (/url\s*\(|expression\s*\(|javascript:/i.test(val)) return null;
            return `${prop}: ${val}`;
        })
        .filter(Boolean)
        .join("; ");
}

function clean(node) {
    // Walk children in reverse so removals don't shift indices.
    const kids = Array.from(node.childNodes);
    for (const kid of kids) {
        if (kid.nodeType === 3) continue;   // text — keep
        if (kid.nodeType !== 1) { kid.remove(); continue; }   // comment, CDATA, etc.
        const tag = kid.tagName;
        if (!ALLOWED_TAGS.has(tag)) {
            // Unwrap: hoist grand-children into parent, then remove the shell.
            while (kid.firstChild) node.insertBefore(kid.firstChild, kid);
            kid.remove();
            continue;
        }
        // Strip disallowed attributes.
        const allowed = ALLOWED_ATTRS[tag] || new Set();
        for (const attr of Array.from(kid.attributes)) {
            const name = attr.name.toLowerCase();
            if (!allowed.has(name)) {
                kid.removeAttribute(attr.name);
                continue;
            }
            if (name === "href") {
                if (!SAFE_HREF.test(attr.value)) kid.removeAttribute("href");
            } else if (name === "style") {
                const filtered = filterStyle(attr.value);
                if (filtered) kid.setAttribute("style", filtered);
                else kid.removeAttribute("style");
            }
        }
        // Force safe defaults on links.
        if (tag === "A") {
            if (kid.getAttribute("href")) {
                kid.setAttribute("target", "_blank");
                kid.setAttribute("rel", "noopener noreferrer");
            }
        }
        clean(kid);
    }
}

export function sanitizeHtml(html) {
    if (typeof html !== "string" || html === "") return "";
    // Fast path: no tags → return as-is.
    if (!html.includes("<")) return html;
    const tpl = document.createElement("template");
    tpl.innerHTML = html;
    clean(tpl.content);
    return tpl.innerHTML;
}

/**
 * Return an object suitable for spreading into a JSX element so it renders
 * either plain text (safe React path) or HTML (via
 * `dangerouslySetInnerHTML`, sanitized). Pick this over
 * `dangerouslySetInnerHTML` directly to avoid double-escaping plain text
 * containing bare `&` or `<` characters.
 *
 *   <h1 className="..." {...richText(d.title)} />
 */
export function richText(value) {
    if (typeof value !== "string") return {};
    if (!value.includes("<")) return { children: value };
    return { dangerouslySetInnerHTML: { __html: sanitizeHtml(value) } };
}
